import {
  Organisation,
  User,
  UserPostRequest,
  UserPutRequest,
} from "callable-data-objects";
import { DBReference } from "callable-data-objects/dist/models/accountDevice/AccountDevice";
import { RecordingFilter } from "callable-data-objects/dist/models/user/RecordingFilter";
import { APIUserContext } from "Providers/APIContexts";
import { APIUserProps } from "Providers/types";
import React, { useState } from "react";
import { useAPI } from "./APIProvider";

type APIUserProviderProps = {
  organisation?: Organisation;
  accountId?: string;
  children: any;
};

const APIUserProvider = ({
  organisation,
  accountId,
  children,
}: APIUserProviderProps) => {
  const api = useAPI();
  const [users, setUsers] = useState<User[]>([]);
  const [user, setUser] = useState<User | undefined>();
  const [loading, setLoading] = useState<boolean>(false);
  const [createUser, setCreateUser] = useState<UserPostRequest | undefined>(
    undefined
  );

  React.useEffect(() => {
    list();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function select(id: string): void {
    api.userService.get(id).then((resp) => {
      setUser(resp);
    });
  }

  function unselect() {
    setUser(undefined);
  }

  function onCreateUser() {
    const recordingFilter: RecordingFilter = {};
    if (organisation) {
      setCreateUser({
        account: {
          id: organisation.id,
          name: organisation.name,
        },
        email: "",
        firstName: "",
        lastName: "",
        groups: [],
        recordingFilter,
      });
    }
  }

  function uncreate() {
    setCreateUser(undefined);
  }

  async function list() {
    if (organisation) {
      api.userService.orgUsers(organisation.id).then((resp: User[]) => {
        setUsers(resp);
      });
    } else if (accountId) {
      api.userService.orgUsers(accountId).then((resp: User[]) => {
        setUsers(resp);
      });
    }
  }

  async function validateEmail(value: string): Promise<String | undefined> {
    let error: string | undefined;
    if (value === null || value === "") error = "Email required";
    if (value.length < 3) error = "Email must be 3 or more characters";
    const resp = await api.userService.exists(value);
    if (resp === "true") {
      error = "Name not unique";
    }
    return Promise.resolve(error);
  }

  async function save(payload: UserPostRequest) {
    setLoading(true);
    const response = await api.userService.post("", payload);
    if (response) {
      select(response.id);
    }
    list();
    uncreate();
    setLoading(false);
  }

  async function update(payload: UserPutRequest) {
    console.log(payload);
    if (user) {
      setLoading(true);
      const response = await api.userService.put(user.id, payload);
      if (response) {
        select(response.id);
      }
      list();
      setLoading(false);
    }
  }

  async function revokeAccount(account: DBReference) {
    if (user) {
      await api.userService.revoke(account.id, user.email);
      select(user.id);
    }
  }

  async function inviteAccount(account: DBReference) {
    if (user) {
      await api.userService.invite(account.id, user.email);
      select(user.id);
    }
  }

  async function revokeRole(role: DBReference) {
    if (user) {
      if (!user.groups) user.groups = [];
      user.groups = user.groups.filter((g) => g !== role.name);
      update(user);
    }
  }

  async function inviteRole(role: DBReference) {
    if (user) {
      if (!user.groups) user.groups = [];
      user.groups.push(role.name);
      update(user);
    }
  }

  async function remove(id: string) {
    setLoading(true);
    await api.userService.delete(id);
    list();
    unselect();
    setLoading(false);
  }

  const orgProps: APIUserProps = {
    users,
    user,
    save,
    update,
    validateEmail,
    select,
    unselect,
    uncreate,
    loading,
    remove,
    revokeAccount,
    inviteAccount,
    revokeRole,
    inviteRole,
    organisation,
    createUser,
    onCreateUser,
  };

  return (
    <APIUserContext.Provider value={orgProps}>
      {children}
    </APIUserContext.Provider>
  );
};

export default APIUserProvider;
